/**
 * Colour palette.
 *
 * Values resolve to the design tokens declared in `config/theme/tokens.css`.
 * Every consumer of this module feeds the value into a CSS context (inline
 * `style` objects, styled-components, antd `style` props, react-icons `color`
 * — which is applied as `style.color`), so custom properties are valid here
 * and let a single `.dark` class on <html> re-theme the whole app.
 *
 * Because the tokens are already theme-aware, `getColors(isDarkMode)` no
 * longer needs to branch. Both maps are kept so the existing API — including
 * `useDarkModeValue` — continues to work unchanged. Raw hex palettes remain
 * exported for the rare consumer that needs a literal colour (canvas, chart
 * datasets, `<meta name="theme-color">`).
 */

// Scrims and overlays are deliberately literal: they sit above the themed
// surfaces rather than participating in them.
const Overlays = {
  PlaceHolder: (opacity = '0.5') => `rgba(235, 235, 235, ${opacity})`,
  BlackOpacity: (opacity = '0.5') => `rgba(8, 10, 18, ${opacity})`,
  WhiteOpacity: (opacity = '0.5') => `rgba(255, 255, 255, ${opacity})`,
};

const TokenColors = {
  ...Overlays,

  Transparent: 'transparent',

  // Brand
  Primary: 'var(--accent)',
  MainColor: 'var(--accent)',
  PrimaryStrong: 'var(--accent-strong)',
  PrimarySoft: 'var(--accent-soft)',

  // Type
  Black: 'var(--text)',
  Secondary: 'var(--text)',
  Text: 'var(--text)',
  // `Text.Basic` falls back to this when no `color` prop is given. Kept as
  // `inherit` so a colour supplied via className (e.g. `text-white` on an
  // accent bubble) still wins over the styled-components declaration.
  TextColor: 'inherit',
  Gray: 'var(--muted)',
  LightGray: 'var(--panel-2)',
  DarkGray: 'var(--rail)',

  // Surfaces
  White: 'var(--panel)',
  LightWhite: 'var(--panel-2)',
  backGroundColor: 'var(--bg)',
  CallingBackground: 'var(--bg)',

  // Lines
  borderColor: 'var(--border)',
  borderStrong: 'var(--border-strong)',

  // Status. Destructive states resolve to the dedicated `--danger` scale rather
  // than aliasing the accent. The brand is red too, so `--danger` is a heavier
  // red than a different hue — the point is that destructive UI can be retuned
  // in one place without touching the brand.
  Danger: 'var(--danger)',
  Error: 'var(--danger)',
  // Legacy name for the old brand red. Every remaining call site is a loading
  // spinner, which is progress rather than a failure — so it stays on brand.
  redColor: 'var(--accent)',
  Green: 'var(--ok)',
  Success: 'var(--ok)',
  Warning: 'var(--warn)',

  ModalCancelColor: 'var(--faint)',
};

/**
 * Literal values, mirroring `tokens.css`. Use only where a CSS custom
 * property cannot be resolved.
 */
const LightPalette = {
  bg: '#f5f6fa',
  panel: '#ffffff',
  panel2: '#f2f4f9',
  panel3: '#e8ebf3',
  rail: '#12141c',
  text: '#141824',
  muted: '#59627a',
  faint: '#868fa3',
  border: '#e4e7f0',
  borderStrong: '#ccd2e0',
  accent: '#ef3742',
  accentStrong: '#c81c27',
  accentSoft: '#ffe5e7',
  ok: '#12a45b',
  warn: '#c2701a',
  danger: '#d81f2c',
  dangerSoft: '#fdecee',
};

const DarkPalette = {
  bg: '#0d0f14',
  panel: '#14171e',
  panel2: '#1b1f28',
  panel3: '#242935',
  rail: '#0a0c10',
  text: '#e9edf4',
  muted: '#9fa9bb',
  faint: '#7a8496',
  border: '#232833',
  borderStrong: '#333a48',
  accent: '#ff5c65',
  accentStrong: '#ff9098',
  accentSoft: '#3a1b20',
  ok: '#46d39a',
  warn: '#f5b544',
  danger: '#ff5c65',
  dangerSoft: '#2b161b',
};

/**
 * Avatar / group tints. Deterministic per conversation so a given chat keeps
 * the same colour between sessions.
 *
 * These are the design's eight hues — periwinkle, teal, amber, slate, violet,
 * blue, green, pink — each taken to the darkest shade of its hue rather than
 * the display swatch. Initials are always white, and the swatches as drawn sat
 * between 1.9:1 and 3.2:1 against white; every value here clears 4:1.
 *
 * The design's fourth swatch was a coral. With a red brand it read as an alert
 * rather than as a person, so it is a slate here — no avatar should look like a
 * missed call.
 */
const AvatarTints = [
  '#5b63e8',
  '#0f7d75',
  '#a86a1e',
  '#41608f',
  '#7b52e0',
  '#2a7fb8',
  '#2f8f5c',
  '#b8478f',
];

const tintFor = (seed = '') => {
  const key = String(seed);
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return AvatarTints[hash % AvatarTints.length];
};

// Retained for backward compatibility — both themes resolve through the same
// token layer, so the returned map is identical.
const LightModeColors = TokenColors;
const DarkModeColors = TokenColors;

const getColors = (_isDarkMode = false) => TokenColors;

const Colors = TokenColors;

export {
  getColors,
  LightModeColors,
  DarkModeColors,
  LightPalette,
  DarkPalette,
  AvatarTints,
  tintFor,
};
export default Colors;
